import { AppPhase } from '../types';

/**
 * App-shell mode foundation (Phase 1A).
 *
 * Adds the feature flag, phase predicates, and pure route/hash helpers for the
 * new dashboard + app navigation model described in
 * docs/DASHBOARD_APP_NAVIGATION_PLAN.md.
 *
 * IMPORTANT: This module is intentionally inert at runtime in Phase 1A. Nothing
 * here is wired into App.tsx routing or rendering yet. When `VITE_APP_SHELL` is
 * off (the default), the app behaves exactly as it does today. This helper is
 * designed to *compose with* `GRADING_ONLY_MODE` later, not replace it.
 *
 * Enable with `VITE_APP_SHELL=true` (mirrors the env parsing style of
 * {@link ../lib/gradingOnlyMode}). Any other value (including unset) is OFF.
 */
function readAppShellEnv(): boolean {
  const raw = import.meta.env.VITE_APP_SHELL;
  return raw === 'true';
}

/** True only when `VITE_APP_SHELL` is explicitly enabled. Defaults OFF. */
export const APP_SHELL_ENABLED = readAppShellEnv();

/** The set of new app-shell phases. */
const APP_SHELL_PHASES = new Set<AppPhase>([
  AppPhase.HOME,
  AppPhase.GRADE_HOME,
  AppPhase.PLAN_HOME,
  AppPhase.COMMUNICATE_HOME,
  AppPhase.MANAGE_HOME,
]);

/** Returns the app-shell feature flag value. */
export function isAppShellEnabled(): boolean {
  return APP_SHELL_ENABLED;
}

/**
 * True only for the new app-shell phases (HOME, GRADE_HOME, PLAN_HOME,
 * COMMUNICATE_HOME, MANAGE_HOME). Legacy dormant shell phases (DASHBOARD, PLAN,
 * RECORDS, CLASSROOM, SCHEDULE, OPTIONS) are intentionally NOT included.
 */
export function isAppShellPhase(phase: AppPhase): boolean {
  return APP_SHELL_PHASES.has(phase);
}

/** The app-shell landing phase. */
export function appHomePhase(): AppPhase {
  return AppPhase.HOME;
}

/** Canonical hashes for the app-shell phases. */
const HASH_TO_PHASE: ReadonlyArray<readonly [string, AppPhase]> = [
  ['#/home', AppPhase.HOME],
  ['#/grade', AppPhase.GRADE_HOME],
  ['#/plan', AppPhase.PLAN_HOME],
  ['#/communicate', AppPhase.COMMUNICATE_HOME],
  ['#/manage', AppPhase.MANAGE_HOME],
  // Privacy 6B: Manage sub-screen. Intentionally NOT added to APP_SHELL_PHASES
  // (it is not a top-level workspace tab), but it gets a hash so deep links and
  // navigateAppShell work and Back returns to #/manage.
  // Privacy 6F: nested under Manage → Privacy. Listed before #/manage/privacy so
  // a future prefix matcher would not mis-route; today matching is exact.
  ['#/manage/privacy/compliance', AppPhase.COMPLIANCE_STATUS],
  ['#/manage/privacy', AppPhase.PRIVACY_DASHBOARD],
];

const PHASE_TO_HASH = new Map<AppPhase, string>(
  HASH_TO_PHASE.map(([hash, phase]) => [phase, hash])
);

function normalizeHash(hash: string): string {
  if (!hash) return '';
  const trimmed = hash.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  // Drop a trailing slash (but keep the leading "#/"): "#/home/" -> "#/home".
  return withHash.length > 2 && withHash.endsWith('/') ? withHash.slice(0, -1) : withHash;
}

/** True when `hash` maps to one of the app-shell phases. */
export function isAppShellHash(hash: string): boolean {
  const normalized = normalizeHash(hash);
  return HASH_TO_PHASE.some(([candidate]) => candidate === normalized);
}

/**
 * Pure hash -> phase mapping for app-shell routes. Returns `null` for unknown or
 * empty hashes so callers can decide how to fall back (e.g. to {@link appHomePhase}).
 * Not wired into runtime routing in Phase 1A.
 */
export function appShellPhaseFromHash(hash: string): AppPhase | null {
  const normalized = normalizeHash(hash);
  const match = HASH_TO_PHASE.find(([candidate]) => candidate === normalized);
  return match ? match[1] : null;
}

/**
 * Pure phase -> hash mapping for app-shell routes. Returns `null` for any phase
 * that is not an app-shell phase. Not wired into runtime routing in Phase 1A.
 */
export function hashForAppShellPhase(phase: AppPhase): string | null {
  return PHASE_TO_HASH.get(phase) ?? null;
}
